<script setup>
import { computed, ref, shallowRef, watch } from 'vue';
import { useStore } from 'vuex';
import UiCard from '@/components/ui/UiCard.vue';
import UiFormDialog from '@/components/ui/UiFormDialog.vue';
import UiPostFeed from '@/components/ui/UiPostFeed.vue';
import { apiRequest } from '@/api/http';
import { toFeedPosts, useAnnouncementComments } from '@/composables/useAnnouncementComments';
import { useAsyncAction } from '@/composables/useAsyncAction';
import { useListResource } from '@/composables/useListResource';
import { useSessionGuard } from '@/composables/useSessionGuard';
import { RoleCode } from '@/constants/accounts';

useSessionGuard();

const store = useStore();
const year = computed(() => store.getters.getFilterSelected);
// Parents read and comment; everyone else can also post.
const canPost = computed(() => store.getters.activeRoleCode !== RoleCode.Parent);

const { items: posts, loading, error, load } = useListResource(async () =>
  toFeedPosts(await apiRequest('get', `/announcement/getMyAnnouncement/${year.value}`)),
);
watch(year, load, { immediate: true });

const { sending, comment, canDelete, removeComment } = useAnnouncementComments(load);

// New announcement
const postOpen = shallowRef(false);
const postForm = ref({ text: '' });
const postFields = [
  { key: 'text', label: 'Message', type: 'textarea', required: true, placeholder: 'Share an update with the school…' },
];
const { busy, run } = useAsyncAction();

function openPost() {
  postForm.value = { text: '' };
  postOpen.value = true;
}

async function publish() {
  const payload = { title: postForm.value.text.trim(), teacherID: store.state.user.id, school_yearID: year.value };
  const ok = await run(
    () => apiRequest('post', '/announcement/', { data: JSON.stringify(payload) }),
    { success: 'Announcement posted.', error: 'Could not post the announcement.' },
  );
  if (ok) {
    postOpen.value = false;
    load();
  }
}
</script>

<template>
  <UiCard title="Announcements" description="Updates shared with the school this school year.">
    <template v-if="canPost" #toolbar>
      <div class="ui-card__toolbar-end">
        <v-btn color="primary" prepend-icon="mdi-plus" @click="openPost">New announcement</v-btn>
      </div>
    </template>

    <v-alert v-if="error" type="error" variant="tonal" density="compact" class="mb-4">
      {{ error }}
    </v-alert>

    <UiPostFeed
      :posts="posts"
      :loading="loading"
      :sending="sending"
      empty-title="No announcements yet"
      :empty-description="canPost ? 'Post the first update for this school year.' : 'Check back later for school updates.'"
      @comment="comment"
          :can-delete-comment="canDelete"
          @delete-comment="removeComment"
    />
  </UiCard>

  <UiFormDialog
    v-model="postOpen"
    title="New announcement"
    description="Everyone in the school will see this."
    size="md"
    v-model:values="postForm"
    :fields="postFields"
    :saving="busy"
    submit-text="Post"
    @submit="publish"
  />
</template>
